import { Input, MaskedInput } from '@pay-com/components'
import {
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
  Controller
} from 'react-hook-form'
import { ChangeEvent, HTMLInputTypeAttribute, ReactNode } from 'react'
import { getErrorMessage } from 'utils/get-error-message'
import { useTranslation } from 'react-i18next'

interface ControlledInputProps<T extends FieldValues> {
  name: FieldPath<T>
  label?: string
  type?: HTMLInputTypeAttribute
  htmlType?: HTMLInputTypeAttribute
  placeholder?: string
  step?: string
  disabled?: boolean
  isLoading?: boolean
  className?: string
  icon?: ReactNode
  format?: string
  mask?: string
  rules?: RegisterOptions<T>
}

export const ControlledInput = <T extends FieldValues>({
  name,
  label,
  type,
  htmlType,
  placeholder,
  step,
  disabled,
  isLoading,
  className,
  icon,
  format,
  mask
}: ControlledInputProps<T>) => {
  const { control } = useFormContext<T>()
  const inputType = type ?? htmlType
  const { t } = useTranslation('errors')

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState

        const commonProps = {
          ...field,
          value: field.value ?? '',
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            field.onChange(e.target.value),
          label,
          placeholder,
          disabled: disabled || isLoading,
          error: Boolean(error),
          errorMessage: getErrorMessage(t, error?.message),
          className,
          icon,
          inputSize: 'medium' as const,
          containerProps: { style: { width: '100%' } }
        }

        if (format) {
          return <MaskedInput {...commonProps} format={format} mask={mask} />
        }

        return <Input {...commonProps} type={inputType} step={step} />
      }}
    />
  )
}
